import React, { useState, useEffect } from "react";
import { onSnapshot, doc, collection, query, where } from "firebase/firestore";
import { db } from "../firebase/config";
import { placeOrderWithToken } from "../firebase/orderHelper";
import { MENU, CATEGORY_ICONS } from "../data/menu";
import { TRANSLATIONS } from "../data/translations";

const SHOP_ID = process.env.REACT_APP_SHOP_ID || "jb-motavarachha";
const SHOP_NAME_GU = process.env.REACT_APP_SHOP_NAME_GU || "જે.બી. ગોળાવાળા";
const SHOP_LOCATION = process.env.REACT_APP_SHOP_LOCATION || "Self Service • Mota Varachha";

const FLOW = ["pending", "approved", "paid", "preparing", "ready", "complete"];

// Build translated STATUS_CONFIG
const getCustomerStatusConfig = (lang) => {
    const T = TRANSLATIONS[lang] || TRANSLATIONS.en;
    return {
        pending: { label: T.cs_pending_label, emoji: "🕐", color: "#ff6b35", desc: T.cs_pending_desc },
        approved: { label: T.cs_approved_label, emoji: "✅", color: "#2196f3", desc: T.cs_approved_desc },
        paid: { label: T.cs_paid_label, emoji: "💰", color: "#9c27b0", desc: T.cs_paid_desc },
        preparing: { label: T.cs_preparing_label, emoji: "🍧", color: "#ff9800", desc: T.cs_preparing_desc },
        ready: { label: T.cs_ready_label, emoji: "🔔", color: "#4caf50", desc: T.cs_ready_desc },
        complete: { label: T.cs_complete_label, emoji: "🎉", color: "#4caf50", desc: T.cs_complete_desc },
        cancelled: { label: T.cs_cancelled_label, emoji: "❌", color: "#f44336", desc: T.cs_cancelled_desc },
    };
};

// ── Live Order Status Screen ──
function OrderStatus({ orderId, tokenNumber, onNewOrder, preparingTokens = [], language }) {
    const [order, setOrder] = useState(null);
    const T = TRANSLATIONS[language] || TRANSLATIONS.en;
    const t = (key) => T[key] ?? TRANSLATIONS.en[key] ?? key;
    const STATUS_CONFIG = getCustomerStatusConfig(language);

    useEffect(() => {
        const unsub = onSnapshot(doc(db, "orders", orderId), (snap) => {
            if (snap.exists()) setOrder({ id: snap.id, ...snap.data() });
        });
        return unsub;
    }, [orderId]);

    if (!order) return (
        <div className="c-success">
            <div className="c-success-card">
                <div className="c-success-icon">⏳</div>
                <p>{t("c_loading")}</p>
            </div>
        </div>
    );

    const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
    const currentStep = FLOW.indexOf(order.status);
    const isComplete = order.status === "complete";

    return (
        <div className="c-app" style={{ paddingBottom: 24 }}>
            <header className="c-header">
                <div className="c-brand">
                    <div className="c-brand-name">{SHOP_NAME_GU}</div>
                    <div className="c-brand-sub">{t("c_order_status")}</div>
                </div>
            </header>

            <div className="c-status-body">
                {/* Token + Status hero */}
                <div className="c-status-hero" style={{ borderColor: cfg.color }}>
                    <div className="c-status-emoji">{cfg.emoji}</div>
                    <div className="c-status-token" style={{ color: cfg.color }}>Token #{tokenNumber}</div>
                    <div className="c-status-label" style={{ color: cfg.color }}>{cfg.label}</div>
                    <div className="c-status-desc">{cfg.desc}</div>
                    <div className="c-status-name">{order.customerName}</div>
                    <div className="c-live-dot">
                        <span className="c-live-pulse" />
                        {t("c_live")}
                    </div>
                </div>

                {/* Now Making banner */}
                {preparingTokens.length > 0 && (
                    <div className="c-preparing-banner">
                        <span className="c-preparing-icon">🍧</span>
                        <span className="c-preparing-text">
                            {t("c_now_making")} {preparingTokens.map(tok => `#${tok}`).join(", ")}
                        </span>
                    </div>
                )}

                {/* Progress steps */}
                <div className="c-progress-wrap">
                    {FLOW.map((s, idx) => {
                        const sCfg = STATUS_CONFIG[s];
                        const isDone = idx <= currentStep;
                        const isCurrent = idx === currentStep;
                        return (
                            <div key={s} className="c-prog-step">
                                {idx > 0 && (
                                    <div className="c-prog-line" style={{ background: idx <= currentStep ? STATUS_CONFIG[FLOW[idx - 1]].color : "#e0e0e0" }} />
                                )}
                                <div className="c-prog-dot-wrap">
                                    <div
                                        className="c-prog-dot"
                                        style={{ background: isDone ? sCfg.color : "#e0e0e0", boxShadow: isCurrent ? `0 0 0 4px ${sCfg.color}30` : "none" }}
                                    >
                                        {isDone && !isCurrent && <span style={{ fontSize: 10 }}>✓</span>}
                                        {isCurrent && <div className="c-prog-pulse" style={{ background: sCfg.color }} />}
                                    </div>
                                    <div className="c-prog-label" style={{ color: isDone ? sCfg.color : "#ccc", fontWeight: isCurrent ? 800 : 500 }}>
                                        {sCfg.label.replace("! 🔔", "").replace("!", "")}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Order items */}
                <div className="c-status-items">
                    <div className="c-status-items-title">{t("c_your_order")}</div>
                    {order.items?.map((item, i) => (
                        <div key={i} className="c-status-item">
                            <div>
                                <span style={{ fontWeight: 700 }}>{item.qty}x</span> {item.nameGu || item.name}
                                {item.category && <span className="c-status-item-cat"> · {item.category}</span>}
                            </div>
                            <span>₹{item.price * item.qty}</span>
                        </div>
                    ))}
                    <div className="c-status-total">
                        <span>{t("total")}</span>
                        <span>₹{order.totalAmount}</span>
                    </div>
                </div>

                {/* Order again — only after complete */}
                {isComplete && (
                    <button className="c-btn-primary" onClick={onNewOrder}>
                        {t("c_order_again")}
                    </button>
                )}
            </div>
        </div>
    );
}

// ── Main Customer App ──
export default function CustomerApp() {
    const [cart, setCart] = useState({});
    const [activeCategory, setActiveCategory] = useState(Object.keys(MENU)[0]);
    const [view, setView] = useState("menu");
    const [customerName, setCustomerName] = useState("");
    const [placedOrderId, setPlacedOrderId] = useState(null);
    const [placedToken, setPlacedToken] = useState(null);
    const [placing, setPlacing] = useState(false);
    const [popularItemIds, setPopularItemIds] = useState(new Set());
    const [stockStatus, setStockStatus] = useState({});
    const [preparingTokens, setPreparingTokens] = useState([]);
    const [language, setLanguage] = useState("en"); // set from Firestore shopSettings

    // ── Translation helper ──
    const T = TRANSLATIONS[language] || TRANSLATIONS.en;
    const t = (key) => T[key] ?? TRANSLATIONS.en[key] ?? key;
    const STATUS_CONFIG = getCustomerStatusConfig(language);

    // ── Listen to shop language setting ──
    useEffect(() => {
        const unsub = onSnapshot(doc(db, "shopSettings", SHOP_ID), (snap) => {
            if (snap.exists() && snap.data().language) {
                setLanguage(snap.data().language);
            }
        });
        return unsub;
    }, []);

    // ── Listen to stock status ──
    useEffect(() => {
        const unsub = onSnapshot(doc(db, "stockStatus", SHOP_ID), (snap) => {
            setStockStatus(snap.exists() ? snap.data() : {});
        });
        return unsub;
    }, []);

    // ── Listen to popular items (top 3 per category) ──
    useEffect(() => {
        const q = query(collection(db, "orders"), where("shopId", "==", SHOP_ID));
        const unsub = onSnapshot(q, (snap) => {
            const itemCounts = {};
            const itemCategory = {};
            snap.docs.forEach(d => {
                d.data().items?.forEach(item => {
                    if (item.id) {
                        itemCounts[item.id] = (itemCounts[item.id] || 0) + item.qty;
                        if (item.category) itemCategory[item.id] = item.category;
                    }
                });
            });
            const byCategory = {};
            Object.entries(itemCounts).forEach(([id, qty]) => {
                const cat = itemCategory[id] || "Other";
                if (!byCategory[cat]) byCategory[cat] = [];
                byCategory[cat].push([id, qty]);
            });
            const popularIds = [];
            Object.values(byCategory).forEach(entries => {
                entries.sort((a, b) => b[1] - a[1]).slice(0, 3).forEach(([id]) => popularIds.push(id));
            });
            setPopularItemIds(new Set(popularIds));
        });
        return unsub;
    }, []);

    // ── Listen to preparing tokens ──
    useEffect(() => {
        const q = query(
            collection(db, "orders"),
            where("shopId", "==", SHOP_ID),
            where("status", "==", "preparing")
        );
        const unsub = onSnapshot(q, (snap) => {
            const tokens = snap.docs
                .map(d => d.data().tokenNumber)
                .filter(Boolean)
                .sort((a, b) => a - b);
            setPreparingTokens(tokens);
        });
        return unsub;
    }, []);

    const categories = Object.keys(MENU);

    const addToCart = (item, category) => {
        setCart((prev) => ({
            ...prev,
            [item.id]: {
                ...item,
                qty: (prev[item.id]?.qty || 0) + 1,
                category: category || prev[item.id]?.category || "",
            },
        }));
    };

    const removeFromCart = (itemId) => {
        setCart((prev) => {
            const updated = { ...prev };
            if (updated[itemId].qty <= 1) delete updated[itemId];
            else updated[itemId].qty -= 1;
            return updated;
        });
    };

    const cartItems = Object.values(cart);
    const cartCount = cartItems.reduce((s, i) => s + i.qty, 0);
    const cartTotal = cartItems.reduce((s, i) => s + i.qty * i.price, 0);

    const placeOrder = async () => {
        if (!customerName.trim()) return alert(t("c_name_placeholder"));
        if (cartItems.length === 0) return alert(t("c_cart_empty"));
        setPlacing(true);
        try {
            const { token, docId } = await placeOrderWithToken(SHOP_ID, {
                shopId: SHOP_ID,
                customerName: customerName.trim(),
                items: cartItems.map((i) => ({
                    id: i.id,
                    name: i.nameEn,
                    nameGu: i.name,
                    price: i.price,
                    qty: i.qty,
                    category: i.category || "",
                })),
                totalAmount: cartTotal,
            });
            setPlacedOrderId(docId);
            setPlacedToken(token);
            setView("status");
        } catch (e) {
            alert("Error placing order. Try again.");
            console.error(e);
        }
        setPlacing(false);
    };

    const resetAll = () => {
        setCart({});
        setCustomerName("");
        setPlacedOrderId(null);
        setPlacedToken(null);
        setView("menu");
    };

    // ── Live Status ──
    if (view === "status" && placedOrderId) {
        return (
            <OrderStatus
                orderId={placedOrderId}
                tokenNumber={placedToken}
                onNewOrder={resetAll}
                preparingTokens={preparingTokens}
                language={language}
            />
        );
    }

    // ── Cart ──
    if (view === "cart") {
        return (
            <div className="c-app">
                <header className="c-header">
                    <button className="c-back-btn" onClick={() => setView("menu")}>← Back</button>
                    <div className="c-header-title">{t("c_your_cart")}</div>
                </header>
                <div className="c-cart-body">
                    {cartItems.length === 0 ? (
                        <div className="c-empty-cart">
                            <div style={{ fontSize: 48 }}>🛒</div>
                            <p>{t("c_cart_empty")}</p>
                            <button className="c-btn-primary" onClick={() => setView("menu")}>{t("c_add_items_btn")}</button>
                        </div>
                    ) : (
                        <>
                            <div className="c-cart-items">
                                {cartItems.map((item) => (
                                    <div key={item.id} className="c-cart-item">
                                        <div className="c-cart-item-info">
                                            <div className="c-cart-item-name">{item.name}</div>
                                            {item.category && <div className="c-cart-item-category">{item.category}</div>}
                                            <div className="c-cart-item-price">₹{item.price} each</div>
                                        </div>
                                        <div className="c-qty-controls">
                                            <button onClick={() => removeFromCart(item.id)}>−</button>
                                            <span>{item.qty}</span>
                                            <button onClick={() => addToCart(item, item.category)}>+</button>
                                        </div>
                                        <div className="c-cart-item-total">₹{item.price * item.qty}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="c-cart-summary">
                                <div className="c-total-row">
                                    <span>{t("total")}</span>
                                    <span className="c-total-amount">₹{cartTotal}</span>
                                </div>
                            </div>
                            <div className="c-name-section">
                                <label>{t("c_your_name_label")}</label>
                                <input
                                    className="c-name-input"
                                    type="text"
                                    placeholder={t("c_name_placeholder")}
                                    value={customerName}
                                    onChange={(e) => setCustomerName(e.target.value)}
                                />
                            </div>
                            <button className="c-btn-primary" onClick={placeOrder} disabled={placing}>
                                {placing ? t("c_placing_order") : `${t("c_place_order_btn")} • ₹${cartTotal}`}
                            </button>
                            <p className="c-pay-note">{t("c_pay_note")}</p>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // ── Menu ──
    return (
        <div className="c-app">
            <header className="c-header">
                <div className="c-brand">
                    <div className="c-brand-name">{SHOP_NAME_GU}</div>
                    <div className="c-brand-sub">{SHOP_LOCATION}</div>
                </div>
                {cartCount > 0 && (
                    <button className="c-cart-btn" onClick={() => setView("cart")}>
                        🛒 <span className="c-cart-badge">{cartCount}</span>
                        <span className="c-cart-total">₹{cartTotal}</span>
                    </button>
                )}
            </header>

            {/* Now Making banner */}
            {preparingTokens.length > 0 && (
                <div className="c-preparing-banner">
                    <span className="c-preparing-icon">🍧</span>
                    <span className="c-preparing-text">
                        {t("c_now_making")} {preparingTokens.map(tok => `#${tok}`).join(", ")}
                    </span>
                </div>
            )}

            <div className="c-tabs">
                {categories.map((cat) => (
                    <button
                        key={cat}
                        className={`c-tab ${activeCategory === cat ? "active" : ""}`}
                        onClick={() => setActiveCategory(cat)}
                    >
                        {CATEGORY_ICONS[cat]} {cat}
                    </button>
                ))}
            </div>

            <div className="c-menu-list">
                {MENU[activeCategory].map((item) => {
                    const inCart = cart[item.id];
                    const isPopular = popularItemIds.has(item.id);
                    const inStock = stockStatus[item.id] !== false;
                    return (
                        <div key={item.id} className={`c-menu-item ${isPopular && inStock ? "c-menu-item-popular" : ""} ${!inStock ? "c-menu-item-oos" : ""}`}>
                            <div className="c-menu-item-info">
                                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                    <div className="c-menu-item-name" style={{ color: inStock ? "var(--text)" : "#bbb" }}>{item.name}</div>
                                    {isPopular && inStock && <span className="c-popular-badge">🔥 Popular</span>}
                                    {!inStock && <span className="c-oos-badge">Sold Out</span>}
                                </div>
                                <div className="c-menu-item-subname">{item.nameEn}</div>
                                <div className="c-menu-item-price" style={{ color: inStock ? "var(--brand)" : "#bbb" }}>₹{item.price}</div>
                            </div>
                            <div className="c-menu-item-action">
                                {!inStock ? (
                                    <div className="c-oos-label">❌</div>
                                ) : inCart ? (
                                    <div className="c-qty-controls">
                                        <button onClick={() => removeFromCart(item.id)}>−</button>
                                        <span>{inCart.qty}</span>
                                        <button onClick={() => addToCart(item, activeCategory)}>+</button>
                                    </div>
                                ) : (
                                    <button className="c-add-btn" onClick={() => addToCart(item, activeCategory)}>
                                        + Add
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {cartCount > 0 && (
                <div className="c-sticky-bar" onClick={() => setView("cart")}>
                    <span>{cartCount} {t("c_items_in_cart")}</span>
                    <span>{t("c_view_cart")} • ₹{cartTotal} →</span>
                </div>
            )}
        </div>
    );
}